import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../config';
import { useNavigate } from 'react-router-dom';
import IngredientAutocomplete from '../components/IngredientAutocomplete';
import '../styles/mealPlanningPreferences.css';
import '../styles/mealPlanningModern.css';

const MealPlanningPreferences = () => {
    const navigate = useNavigate();
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [newAllergen, setNewAllergen] = useState('');
    const [newDislikedFood, setNewDislikedFood] = useState('');

    const [dietaryRestrictions, setDietaryRestrictions] = useState({
        vegetarian: false,
        vegan: false,
        glutenFree: false,
        dairyFree: false,
        nutFree: false,
        shellfishFree: false,
        eggFree: false,
        soyFree: false
    });

    const [allergies, setAllergies] = useState([]);
    const [planningFrequency, setPlanningFrequency] = useState('weekly');
    const [planFlexibility, setPlanFlexibility] = useState('flexible');
    const [mealsPerDay, setMealsPerDay] = useState(3);
    const [snacksPerDay, setSnacksPerDay] = useState(1);
    const [budgetConstraint, setBudgetConstraint] = useState('');
    const [ingredientPreference, setIngredientPreference] = useState('mid-range');
    const [costSavingGoal, setCostSavingGoal] = useState('');
    const [favoriteCuisines, setFavoriteCuisines] = useState([]);
    const [dislikedFoods, setDislikedFoods] = useState([]);
    const [cookingTimeAvailable, setCookingTimeAvailable] = useState({
        monday: 30, tuesday: 30, wednesday: 30, thursday: 30, friday: 30, saturday: 60, sunday: 60
    });
    const [mealPrepDays, setMealPrepDays] = useState([]);
    const [preferredStores, setPreferredStores] = useState([]);
    const [preferLocalSeasonal, setPreferLocalSeasonal] = useState(false);
    const [minimizeWaste, setMinimizeWaste] = useState(true);

    useEffect(() => {
        loadPreferences();
    }, []);

    const loadPreferences = async () => {
        try {
            const response = await axiosInstance.get('/meal-planning/preferences');
            if (response.data.success && response.data.preferences) {
                const prefs = response.data.preferences;
                if (prefs.dietaryRestrictions) setDietaryRestrictions(prefs.dietaryRestrictions);
                if (prefs.allergies) setAllergies(prefs.allergies);
                if (prefs.planningFrequency) setPlanningFrequency(prefs.planningFrequency);
                if (prefs.planFlexibility) setPlanFlexibility(prefs.planFlexibility);
                if (prefs.mealsPerDay) setMealsPerDay(prefs.mealsPerDay);
                if (prefs.snacksPerDay) setSnacksPerDay(prefs.snacksPerDay);
                if (prefs.budgetConstraint) setBudgetConstraint(prefs.budgetConstraint);
                if (prefs.ingredientPreference) setIngredientPreference(prefs.ingredientPreference);
                if (prefs.costSavingGoal) setCostSavingGoal(prefs.costSavingGoal);
                if (prefs.favoriteCuisines) setFavoriteCuisines(prefs.favoriteCuisines);
                if (prefs.dislikedFoods) setDislikedFoods(prefs.dislikedFoods);
                if (prefs.cookingTimeAvailable) setCookingTimeAvailable(prefs.cookingTimeAvailable);
                if (prefs.mealPrepDays) setMealPrepDays(prefs.mealPrepDays);
                if (prefs.preferredStores) setPreferredStores(prefs.preferredStores);
                if (prefs.preferLocalSeasonal !== undefined) setPreferLocalSeasonal(prefs.preferLocalSeasonal);
                if (prefs.minimizeWaste !== undefined) setMinimizeWaste(prefs.minimizeWaste);
            }
        } catch (error) {
            console.error('Error loading preferences:', error);
        } finally {
            setLoading(false);
        }
    };

    const savePreferences = async () => {
        setSaving(true);
        try {
            const preferences = {
                dietaryRestrictions,
                allergies,
                planningFrequency,
                planFlexibility,
                mealsPerDay,
                snacksPerDay,
                budgetConstraint: budgetConstraint ? parseFloat(budgetConstraint) : null,
                ingredientPreference,
                costSavingGoal: costSavingGoal ? parseFloat(costSavingGoal) : null,
                favoriteCuisines,
                dislikedFoods,
                cookingTimeAvailable,
                mealPrepDays,
                preferredStores,
                preferLocalSeasonal,
                minimizeWaste
            };
            const response = await axiosInstance.post('/meal-planning/preferences', preferences);
            if (response.data.success) alert('Preferences saved successfully!');
        } catch (error) {
            console.error('Error saving preferences:', error);
            alert('Failed to save preferences');
        } finally {
            setSaving(false);
        }
    };

    const toggleDietaryRestriction = (restriction) => {
        setDietaryRestrictions(prev => ({ ...prev, [restriction]: !prev[restriction] }));
    };
    const addAllergy = () => {
        if (!newAllergen.trim()) return;
        const allergenLower = newAllergen.toLowerCase();
        if (!allergies.some(a => a.toLowerCase() === allergenLower)) {
            setAllergies([...allergies, newAllergen.trim()]);
        }
        setNewAllergen('');
    };
    const removeAllergy = (allergy) => setAllergies(allergies.filter(a => a !== allergy));
    const addDislikedFood = () => {
        if (!newDislikedFood.trim()) return;
        const foodLower = newDislikedFood.toLowerCase();
        if (!dislikedFoods.some(f => f.toLowerCase() === foodLower)) {
            setDislikedFoods([...dislikedFoods, newDislikedFood.trim()]);
        }
        setNewDislikedFood('');
    };
    const removeDislikedFood = (food) => setDislikedFoods(dislikedFoods.filter(f => f !== food));
    const toggleCuisine = (cuisine) => {
        if (favoriteCuisines.includes(cuisine)) setFavoriteCuisines(favoriteCuisines.filter(c => c !== cuisine));
        else setFavoriteCuisines([...favoriteCuisines, cuisine]);
    };
    const toggleMealPrepDay = (day) => {
        if (mealPrepDays.includes(day)) setMealPrepDays(mealPrepDays.filter(d => d !== day));
        else setMealPrepDays([...mealPrepDays, day]);
    };
    const updateCookingTime = (day, minutes) => {
        setCookingTimeAvailable(prev => ({ ...prev, [day]: parseInt(minutes) || 0 }));
    };

    const formatLabel = (key) => key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="flex items-center gap-2">
                    <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm text-gray-500">Loading preferences...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
                {/* Header - Mob style */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">Meal planning preferences</h1>
                        <p className="text-sm text-gray-600 mt-0.5">Customize your meal planning</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <button
                            onClick={() => navigate('/meal-planning')}
                            className="px-3 py-2 rounded-full text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
                        >
                            ← Back to Meal Planning
                        </button>
                        <button
                            onClick={() => navigate('/meal-planning/ai')}
                            className="px-3 py-2 rounded-full text-sm font-medium text-white bg-amber-500 hover:bg-amber-600 transition-colors"
                        >
                            AI Meal Planning
                        </button>
                    </div>
                </div>

                <div className="space-y-4 sm:space-y-5">
                    {/* Dietary & Allergies */}
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="p-3 sm:p-4 border-b border-gray-100">
                            <h2 className="text-sm font-semibold text-gray-900">Dietary & allergies</h2>
                            <p className="text-xs text-gray-500 mt-0.5">Restrictions and ingredients to avoid</p>
                        </div>
                        <div className="p-3 sm:p-4 space-y-4">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                                {Object.entries(dietaryRestrictions).map(([key, value]) => (
                                    <label key={key} className="flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                        <input type="checkbox" checked={value} onChange={() => toggleDietaryRestriction(key)} className="rounded border-gray-300 text-amber-600 focus:ring-amber-500" />
                                        <span className="text-xs sm:text-sm text-gray-700">{formatLabel(key)}</span>
                                    </label>
                                ))}
                            </div>
                            <div>
                                <h3 className="text-xs font-medium text-gray-700 mb-1">Allergies</h3>
                                <p className="text-xs text-gray-500 mb-2">Add ingredients to filter from plans.</p>
                                <div className="flex gap-2 mb-2">
                                    <div className="flex-1 min-w-0">
                                        <IngredientAutocomplete value={newAllergen} onChange={setNewAllergen} placeholder="Ingredient..." style={{ flex: 1 }} />
                                    </div>
                                    <button onClick={addAllergy} className="px-3 py-2 rounded-lg text-xs font-medium bg-gray-900 text-white hover:bg-gray-800 shrink-0">Add</button>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {allergies.map(allergy => (
                                        <span key={allergy} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                                            {allergy}
                                            <button type="button" onClick={() => removeAllergy(allergy)} className="text-gray-500 hover:text-gray-700 leading-none">×</button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Planning frequency & flexibility
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="p-3 sm:p-4 border-b border-gray-100">
                            <h2 className="text-sm font-semibold text-gray-900">Planning & flexibility</h2>
                            <p className="text-xs text-gray-500 mt-0.5">Schedule and meal count</p>
                        </div>
                        <div className="p-3 sm:p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Frequency</label>
                                <select value={planningFrequency} onChange={(e) => setPlanningFrequency(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500">
                                    <option value="weekly">Weekly</option>
                                    <option value="bi-weekly">Bi-weekly</option>
                                    <option value="monthly">Monthly</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Flexibility</label>
                                <select value={planFlexibility} onChange={(e) => setPlanFlexibility(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500">
                                    <option value="rigid">Rigid</option>
                                    <option value="flexible">Flexible</option>
                                    <option value="semi-flexible">Semi-flexible</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Meals per day</label>
                                <input type="number" min="1" max="6" value={mealsPerDay} onChange={(e) => setMealsPerDay(parseInt(e.target.value) || 1)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Snacks per day</label>
                                <input type="number" min="0" max="5" value={snacksPerDay} onChange={(e) => setSnacksPerDay(parseInt(e.target.value) || 0)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500" />
                            </div>
                        </div>
                    </div> */}

                    {/* Budget & cost */}
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="p-3 sm:p-4 border-b border-gray-100">
                            <h2 className="text-sm font-semibold text-gray-900">Budget</h2>
                            <p className="text-xs text-gray-500 mt-0.5">How much you want to spend per day on meals</p>
                        </div>
                        <div className="p-3 sm:p-4">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Daily budget (R)</label>
                            <input
                                type="number"
                                min="0"
                                step="5"
                                value={budgetConstraint}
                                onChange={(e) => setBudgetConstraint(e.target.value)}
                                placeholder="e.g. 150"
                                className="w-full sm:w-48 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500"
                            />
                            {budgetConstraint && (
                                <p className="text-xs text-gray-500 mt-1.5">
                                    The AI will keep each day's meals within R{budgetConstraint}, accounting for ingredients you already have in your cupboard.
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Cuisines & disliked foods
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="p-3 sm:p-4 border-b border-gray-100">
                            <h2 className="text-sm font-semibold text-gray-900">Cuisines & foods to avoid</h2>
                            <p className="text-xs text-gray-500 mt-0.5">Favorites and ingredients to filter out</p>
                        </div>
                        <div className="p-3 sm:p-4 space-y-4">
                            <div>
                                <h3 className="text-xs font-medium text-gray-700 mb-2">Favorite cuisines</h3>
                                <div className="flex flex-wrap gap-1.5">
                                    {['Italian', 'Mexican', 'Asian', 'Indian', 'Mediterranean', 'South African', 'American', 'French', 'Thai', 'Japanese'].map(cuisine => (
                                        <button
                                            key={cuisine}
                                            type="button"
                                            onClick={() => toggleCuisine(cuisine)}
                                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${favoriteCuisines.includes(cuisine) ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                        >
                                            {cuisine}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <h3 className="text-xs font-medium text-gray-700 mb-1">Foods to avoid</h3>
                                <div className="flex gap-2 mb-2">
                                    <div className="flex-1 min-w-0">
                                        <IngredientAutocomplete value={newDislikedFood} onChange={setNewDislikedFood} placeholder="Ingredient..." style={{ flex: 1 }} />
                                    </div>
                                    <button onClick={addDislikedFood} className="px-3 py-2 rounded-lg text-xs font-medium bg-gray-900 text-white hover:bg-gray-800 shrink-0">Add</button>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {dislikedFoods.map(food => (
                                        <span key={food} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                                            {food}
                                            <button type="button" onClick={() => removeDislikedFood(food)} className="text-gray-500 hover:text-gray-700 leading-none">×</button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div> */}

                    {/* Schedule & meal prep days
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="p-3 sm:p-4 border-b border-gray-100">
                            <h2 className="text-sm font-semibold text-gray-900">Schedule & timing</h2>
                            <p className="text-xs text-gray-500 mt-0.5">Cooking time per day and prep days</p>
                        </div>
                        <div className="p-3 sm:p-4 space-y-4">
                            <div>
                                <h3 className="text-xs font-medium text-gray-700 mb-2">Cooking time (min per day)</h3>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    {Object.entries(cookingTimeAvailable).map(([day, minutes]) => (
                                        <div key={day}>
                                            <label className="block text-xs text-gray-500 mb-0.5">{day.charAt(0).toUpperCase() + day.slice(1)}</label>
                                            <input type="number" min="0" step="5" value={minutes} onChange={(e) => updateCookingTime(day, e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-amber-500" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <h3 className="text-xs font-medium text-gray-700 mb-2">Meal prep days</h3>
                                <div className="flex flex-wrap gap-1.5">
                                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                                        <button
                                            key={day}
                                            type="button"
                                            onClick={() => toggleMealPrepDay(day)}
                                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${mealPrepDays.includes(day) ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                        >
                                            {day.slice(0, 3)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div> */}

                    {/* Shopping preferences */}
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="p-3 sm:p-4 border-b border-gray-100">
                            <h2 className="text-sm font-semibold text-gray-900">Shopping</h2>
                            <p className="text-xs text-gray-500 mt-0.5">Ingredient and waste preferences</p>
                        </div>
                        <div className="p-3 sm:p-4 space-y-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={preferLocalSeasonal} onChange={(e) => setPreferLocalSeasonal(e.target.checked)} className="rounded border-gray-300 text-amber-600 focus:ring-amber-500" />
                                <span className="text-sm text-gray-700">Prefer local and seasonal ingredients</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={minimizeWaste} onChange={(e) => setMinimizeWaste(e.target.checked)} className="rounded border-gray-300 text-amber-600 focus:ring-amber-500" />
                                <span className="text-sm text-gray-700">Minimize waste (use cupboard first)</span>
                            </label>
                        </div>
                    </div>

                    {/* Save */}
                    <div className="pt-2">
                        <button
                            onClick={savePreferences}
                            disabled={saving}
                            className="w-full py-3 rounded-xl text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 transition-colors"
                        >
                            {saving ? 'Saving...' : 'Save preferences'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MealPlanningPreferences;
